export const MEETINGS = {
  ihes: {
    slug: 'ihes',
    org: 'IHES',
    fullName: 'The Institute of Health Care Executives and Suppliers',
    logo: '/logos/ihes.png',
    logoRatio: 480 / 146,
    title: '2026 Institute for Healthcare Executives Summit',
    subtitle: '48th Annual · September 9–11, 2026',
    dates: 'Sep 9–11, 2026',
    venue: 'The Ritz-Carlton Dallas',
    city: 'Irving, TX',
    color: '#0B3B2E',
    colorSoft: '#E6EEE9',
    accent: '#B87333',
    dateISO: '2026-09-09T08:00:00',
    ordinal: '48th Annual',
  },
  cpes: {
    slug: 'cpes',
    org: 'CPES',
    fullName: 'The Council of Pharmacy Executives and Suppliers',
    logo: '/logos/cpes.png',
    logoRatio: 480 / 146,
    title: '2026 Council of Pharmacy Executives & Suppliers Meeting',
    subtitle: '11th Annual · October 7–9, 2026',
    dates: 'Oct 7–9, 2026',
    venue: 'Hilton Nashville Downtown',
    city: 'Nashville, TN',
    color: '#1E2A78',
    colorSoft: '#E8EAF3',
    accent: '#C8452B',
    dateISO: '2026-10-07T08:00:00',
    ordinal: '11th Annual',
  },
  csce: {
    slug: 'csce',
    org: 'CSCE',
    fullName: 'The Council of Supply Chain Executives',
    logo: '/logos/csce.png',
    logoRatio: 480 / 146,
    title: '2026 Council of Supply Chain Executives Meeting',
    subtitle: '22nd Annual · November 11–13, 2026',
    dates: 'Nov 11–13, 2026',
    venue: 'Hilton Nashville Downtown',
    city: 'Nashville, TN',
    color: '#C86A1F',
    colorSoft: '#F7ECE2',
    accent: '#0B3B2E',
    dateISO: '2026-11-11T08:00:00',
    ordinal: '22nd Annual',
  },
  muia: {
    slug: 'muia',
    org: 'MUIA',
    fullName: 'Med Use Intelligence & Automation',
    logo: '/logos/muia-compact.png',
    logoRatio: 480 / 146,
    title: '2026 Med Use Intelligence & Automation Meeting',
    subtitle: '2nd Annual · a CPES sub-meeting',
    dates: 'Oct 7, 2026',
    venue: 'Hilton Nashville Downtown',
    city: 'Nashville, TN',
    color: '#2D2496',
    colorSoft: '#EAE8F4',
    accent: '#7A97CC',
    dateISO: '2026-10-07T08:00:00',
    ordinal: '2nd Annual',
  },
};

export const MEETING_SLUGS = ['ihes', 'cpes', 'csce', 'muia'];

// URL-safe slug from a company name: "ESP Global" → "esp-global"
export function toCompanySlug(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export const meetingBySlug = (slug) => MEETINGS[slug] || null;

// Map a series shortname (from `meeting_series.name` in Supabase) to the branding
// block — palette, logo, ratios. Used when resolving live events whose portal_slug
// is event-specific (e.g. `ihes2026`) but branding should key off the series.
export function brandingForSeriesName(name) {
  const upper = (name || '').toUpperCase();
  if (upper.includes('CPES')) return MEETINGS.cpes;
  if (upper.includes('CSCE')) return MEETINGS.csce;
  if (upper.includes('MUIA') || upper.includes('MUAI')) return MEETINGS.muia;
  return MEETINGS.ihes;
}

// Derive a "meeting" object suitable for the UI (header + tokens) from a Supabase
// event row. Merges event-specific values (title, venue, dates) with the series
// branding palette/logos.
export function meetingFromEvent(event) {
  if (!event) return null;
  const seriesName = event.meeting_series?.name || event.series_name || 'IHES';
  const branding = brandingForSeriesName(seriesName);
  const slug = event.portal_slug || branding.slug;

  // Build the dates display string.
  const dates = formatEventDates(event.start_date, event.end_date) || branding.dates;
  const city = [event.venue_city, event.venue_state].filter(Boolean).join(', ') || branding.city;

  return {
    ...branding,
    slug,                     // routing slug (event-specific, e.g. 'ihes2026')
    themeSlug: branding.slug, // CSS theme key (one of ihes/cpes/csce/muia)
    title: event.event_name || branding.title,
    subtitle: branding.subtitle,
    dates,
    venue: event.venue_name || branding.venue,
    city,
    dateISO: event.start_date ? `${event.start_date}T08:00:00` : branding.dateISO,
    // Keep raw event around for pages that need event.id / portal columns.
    _event: event,
  };
}

function formatEventDates(start, end) {
  if (!start) return '';
  const s = new Date(start + 'T12:00:00');
  if (!end || end === start) {
    return s.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const e = new Date(end + 'T12:00:00');
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString('en-US', { month: 'short' })} ${s.getDate()}\u2013${e.getDate()}, ${s.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} \u2013 ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export function lighten(hex, amt) {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.round(((n >> 16) & 255) + 255 * amt));
  const g = Math.min(255, Math.round(((n >> 8) & 255) + 255 * amt));
  const b = Math.min(255, Math.round((n & 255) + 255 * amt));
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}
